<template>
  <b-overlay
    :show="$store.state.overlays.header"
    blur=""
    class="app-header"
    no-fade
    opacity="0.5"
    variant="dark"
    wrap-tag="header"
    z-index="0"
  >
    <template #overlay><span> </span></template>
    <b-navbar :class="{'public': isPublicUser}" :toggleable="isPublicUser ? 'xs' : 'lg'" fill role="navigation">
      <!-- Logo and school name -->
      <b-navbar-brand :class="{'mar-l-3': isPublicUser}">
        <img :alt="$t('School logo')" :src="logoSrc">
        <!-- School name -->
        <span
          v-if="isHomeLinkDisabled"
          class="district-name px-2 disabled"
        >
          {{ info.districtName }}
        </span>
        <router-link
          v-else
          class="district-name px-2"
          :to="{name: districtNameRouteName}"
        >
          {{ info.districtName }}
        </router-link>
      </b-navbar-brand>

      <!-- Main navigation menu -->
      <b-nav v-if="(!isPublicUser || isPublicSiteEnabled) && !isTheHeaderMenuHidden" id="nav-main"
             :class="isPublicUser ? 'd-sm-flex' : 'd-lg-flex'">
        <li v-for="(menuItem, index) in navMenu" :key="menuItem.name+index">
          <b-dropdown
            v-if="menuItem.items"
            :class="{ active: menuItem.active }"
            class="h-100"
            no-caret>
            <template #button-content>
              <i v-if="menuItem.icon" :class="[menuItem.icon, 'px-1']" :title="$t(menuItem.name)"></i>
              <span :title="$t(menuItem.name)" class="item-name">{{ $t(menuItem.name) }}</span>
            </template>
            <template v-for="(subMenu, subIndex) in menuItem.items">
              <b-dropdown-item
                :key="subMenu.name+'_'+subIndex"
                :class="{'sub-link': subMenu.subLink}"
                :href="subMenu.vue ? '' : $atlasHelpers.getAtlasRelativeLink(subMenu.url)"
                :target="subMenu.target || '_self'"
                :to="subMenu.vue ? subMenu.url : ''">
                <span v-if="menuItem.name !== '[DEVELOP]'">{{ $t(subMenu.name) }}</span>
                <span v-else v-html="subMenu.name"></span>
                <i v-if="subMenu.icon" :class="[subMenu.icon, 'px-2']"></i>
              </b-dropdown-item>
            </template>
          </b-dropdown>
          <div v-else class="dropdown b-dropdown h-100 btn-group">
            <component
              :is="getComponent(menuItem)"
              :href="menuItem.target === '_blank' ? menuItem.url : ''"
              :target="menuItem.target"
              class="btn dropdown-toggle dropdown-toggle-no-caret"
              @click="menuItem.target === '' && goTo(menuItem.url)"
            >
              <i v-if="menuItem.icon" :class="[menuItem.icon, 'px-1']" :title="$t(menuItem.name)"></i>
              <span :title="$t(menuItem.name)" class="item-name">{{ $t(menuItem.name) }}</span>
            </component>
          </div>
        </li>

      </b-nav>

      <!-- Service navigation menu -->
      <b-nav id="nav-service"
             :class="{'mar-r-3': isPublicUser}">
        <!-- Search -->
        <template v-if="hasSearchBox">
          <b-nav-form id="search-form" @submit.prevent="doSearch">
            <b-form-input v-model="searchQuery" :placeholder="$t('[SEARCH]')" size="sm"/>
            <app-icon-with-action :hoverEffect="false" @click="doSearch">
              <i :title="$t('[SEARCH]')" class="fal fa-search"/>
            </app-icon-with-action>
          </b-nav-form>
          <span class="px-1 d-none d-lg-block"></span>
        </template>
        <!-- Notifications -->
        <li v-if="!isPublicUser">
          <notifications class="align-self-center"/>
        </li>
        <!-- Service menu items -->
        <template v-if="!isPublicUser">
          <li v-for="(menuItem, index) in serviceMenu" :key="menuItem.name+index">
            <b-dropdown
              :class="{ active: menuItem.active }"
              class="h-100"
              no-caret
              :right="!isRTL"
            >
              <template v-slot:button-content>
                <i v-if="menuItem.icon" :class="menuItem.icon" :title="$t(menuItem.name)"></i>
              </template>
              <template v-for="(subMenu, subIndex) in menuItem.items">
                <b-dropdown-item
                  :key="subMenu.name+'_'+subIndex"
                  :href="subMenu.vue ? '' : subMenu.url"
                  :target="subMenu.target || '_self'"
                  :to="subMenu.vue ? subMenu.url : ''">
                  <span>{{ $t(subMenu.name) }}</span>
                </b-dropdown-item>
              </template>
            </b-dropdown>
          </li>
        </template>
        <!-- User settings -->
        <li v-if="info.user && !info.isPLC && !isPublicUser">
          <app-dropdown
            id="user-settings"
            no-caret
            right
            class="d-lg-flex"
            v-b-tooltip:hover
            :title="$t('Your Profile')"
          >
            <template v-slot:button-content>
              {{ userFirstName[0] }}{{ userLastName[0] }}
            </template>
            <header class="user-settings-name">
              {{ userSettingsName }}
            </header>
            <b-dropdown-item :to="{name: 'MySettings'}" v-if="!info.isTestDriveSite">
              <span><i class="fal fa-fw fa-cog"/> {{ $t('My Settings') }}</span>
            </b-dropdown-item>
            <b-dropdown-item href="/Atlas/Authentication/View/Logout">
              <span><i class="fal fa-fw fa-sign-out-alt"/> {{ $t('Sign Out') }}</span>
            </b-dropdown-item>
          </app-dropdown>
        </li>
        <!-- Mobile toggle button -->
        <li>
          <app-icon-with-action
            v-if="!isTheHeaderMenuHidden"
            :class="{ active: mobileMenuShown, 'd-lg-none': !isPublicUser, 'd-sm-none': isPublicUser }"
            :hoverEffect="false"
            :aria-label="$t('Mobile menu toggler')"
            class="menu-toggle"
            @click="toggleMobileMenu">
            <i class="fal fa-bars"/>
          </app-icon-with-action>
        </li>
        <!-- Public site Atlas logo -->
        <li v-if="isPublicUser" class="atlas-logo-public">
          <i class="atlas-logo-public--text">{{ $t('Powered by') }}</i>
          <a href="https://www.onatlas.com/" target="_blank">
            <img :alt="$t('Atlas logo')" class="atlas-logo" src="../assets/images/atlas_logo.png">
          </a>
        </li>
      </b-nav>

      <!-- Mobile navigation menu -->
      <div v-if="mobileMenuShown" id="nav-main-mobile" class="d-lg-none">
        <div
          v-for="menuItem in navMenu"
          :key="menuItem.name"
          class="mobile-nav-item">
          <template v-if="menuItem.items">
            <b-button
              v-b-toggle="`mobile-${menuItem.name.split(' ').join('-')}`"
              :class="{ active: menuItem.active }"
              variant="outline">
              <div class="d-flex align-items-center">
                <i v-if="menuItem.icon" :class="['icon', menuItem.icon]"></i>
                <span class="item-name">{{ $t(menuItem.name) }}</span>
              </div>
              <i class="dropdown-icon fas"></i>
            </b-button>
            <b-collapse :id="`mobile-${menuItem.name.split(' ').join('-')}`" accordion="mobile-menu-accordion">
              <div class="sub-link-wrapper">
                <b-link
                  v-for="(subMenu, subIndex) in menuItem.items"
                  :key="subMenu.name+'_'+subIndex"
                  :class="{'sub-link': subMenu.subLink}"
                  :href="subMenu.vue ? '' : subMenu.url"
                  :target="subMenu.target || '_self'"
                  :to="subMenu.vue ? subMenu.url : ''"
                  class="mobile-nav-sub-link"
                  @click="hideMobileMenu">
                  • <span>{{ $t(subMenu.name) }}</span>
                  <i v-if="subMenu.icon" :class="[subMenu.icon, 'px-2']"
                     :title="subMenu.tooltip ? subMenu.tooltip : ''"/>
                </b-link>
              </div>
            </b-collapse>
          </template>
          <a v-else :href="menuItem.url" :target="menuItem.target">
            <div class="d-flex align-items-center">
              <i v-if="menuItem.icon" :class="['icon', menuItem.icon]"></i>
              <span class="item-name">{{ $t(menuItem.name) }}</span>
            </div>
          </a>
        </div>
        <!-- User menu -->
        <div v-if="info.user && !info.isPLC && !isPublicUser" class="mobile-nav-item">
          <b-button v-b-toggle="`mobile-user-menu`" variant="outline">
            <div class="d-flex align-items-center">
              <i class="icon fal fa-user-circle"/>
              <span>{{ userName }}</span>
            </div>
            <i class="dropdown-icon fas"></i>
          </b-button>
          <b-collapse :id="`mobile-user-menu`" accordion="mobile-menu-accordion">
            <div class="sub-link-wrapper">
              <b-link
                v-if="!info.isTestDriveSite"
                class="mobile-nav-sub-link"
                :to="{name: 'MySettings'}"
                @click="hideMobileMenu">
                <span><i class="fal fa-cog"/> {{ $t('My Settings') }}</span>
              </b-link>
              <b-link
                class="mobile-nav-sub-link"
                href="/Atlas/Authentication/View/Logout"
                target="_blank"
                @click="hideMobileMenu">
                <span><i class="fal fa-sign-out-alt"/> {{ $t('Sign Out') }}</span>
              </b-link>
            </div>
          </b-collapse>
        </div>
      </div>

      <!-- Print view Atlas logo -->
      <div id="atlas-logo-print">
        <img :alt="$t('Atlas logo with label')"
             class="logo"
             src="../assets/images/atlas_logo.png"/>
      </div>
    </b-navbar>
  </b-overlay>
</template>

<script>
import Notifications from '@/components/notificationsNew/Notifications'
import api from '@/services/api.service'
import AppIconWithAction from './htmlHelpers/AppIconWithAction'
import AppDropdown from './htmlHelpers/AppDropdown'
import { mapGetters, mapMutations, mapState } from 'vuex'

export default {
  name: 'TheHeader',
  components: { AppDropdown, AppIconWithAction, Notifications },
  data: function () {
    return {
      searchQuery: '',
      navMenu: [],
      serviceMenu: [],
      mobileMenuShown: false
    }
  },
  computed: {
    ...mapState('InfoStore', ['info', 'shouldReloadHeaderItems', 'isTheHeaderMenuHidden', 'isTheHeaderSearchHidden', 'isHomeLinkDisabled']),
    ...mapGetters('InfoStore', ['isPublicUser', 'publicSiteSettings', 'isPublicSiteEnabled', 'userFirstName', 'userLastName']),
    districtNameRouteName() {
      return this.isPublicUser ? 'PublicHome' : 'MyDashboard'
    },
    hasSearchBox() {
      if (this.isTheHeaderSearchHidden) return false
      return this.isPublicUser ? this.isPublicSiteEnabled && this.publicSiteSettings?.publicSiteAdditionalPagesSearch : true
    },
    logoSrc() {
      return this.info.logo || '/images/school_logo_small.png'
    },
    userName() {
      return `${this.optionalCutName(this.userFirstName)} ${this.optionalCutName(this.userLastName)}`
    },
    userSettingsName() {
      return `${this.userFirstName} ${this.userLastName}`
    },
    isRTL() {
      return document.dir === 'rtl'
    }
  },
  async created() {
    this.loadMenuItems()
  },
  methods: {
    ...mapMutations('InfoStore', ['SET_HEADER_ITEMS_SHOULD_BE_RELOADED']),
    activateSpecificMenuItem(path) {
      let activatedFlag = false

      const checkAndActivateItem = function (nav) {
        if (activatedFlag) {
          nav.active = false
        } else {
          if (path === nav.url && path === window.location.pathname) {
            nav.active = true
            activatedFlag = true
          } else {
            if (nav?.items?.length) {
              const hasPath = nav.items.some(item => {
                if (item.url && !activatedFlag) {
                  return path === item.url.split('?')[0] && path === window.location.pathname
                }
                return false
              })
              if (hasPath) {
                nav.active = true
                activatedFlag = true
              }
            }
          }
        }
      }

      this.navMenu.forEach(checkAndActivateItem)
      this.serviceMenu.forEach(checkAndActivateItem)
    },
    doSearch() {
      const queryParams = this.searchQuery ? `?BackLink=Atlas_Search_View_Default&SearchCurriculum=1&strKey1=${this.searchQuery}` : ''
      window.location = `/Atlas/Search/View/Default${queryParams}`
    },
    async loadMenuItems() {
      const response = await api.system.fetchHeaderMenu()
      const header = response?.data
      if (header?.main?.length) {
        this.navMenu = header.main.map(item => {
          return { ...item, ...{ active: false } }
        })
      }
      if (header?.service?.length) {
        this.serviceMenu = header.service.map(item => {
          return { ...item, ...{ active: false } }
        })
      }
      this.activateSpecificMenuItem(this.$route.path)
    },
    toggleMobileMenu() {
      this.mobileMenuShown = !this.mobileMenuShown
    },
    optionalCutName(name = '') {
      return name.split(' ').map(item => {
        return item.length < 18 ? item : item.slice(0, 17).concat('…')
      }).join(' ')
    },
    goTo(url) {
      if (this.$route.path !== url) this.$router.push(url)
    },
    hideMobileMenu() {
      this.mobileMenuShown = false
    },
    getComponent(menuItem) {
      return menuItem?.target === '_blank' ? 'a' : 'b-button'
    }
  },
  watch: {
    $route(to, from) {
      if (to.path === from.path) return
      this.activateSpecificMenuItem(to.path)
    },
    shouldReloadHeaderItems(shouldReload) {
      if (shouldReload) {
        this.loadMenuItems()
        this.SET_HEADER_ITEMS_SHOULD_BE_RELOADED(false)
      }
    }
  }
}
</script>

<style lang="scss">
#app {
  .app-header {
    height: 4rem;
    background-color: $white;
    box-shadow: $box-gray-shadow;

    .navbar {
      height: 100%;
      background-color: inherit;
      padding: 0;

      * {
        outline-color: var(--scheme-top-nav-txt);
      }

      // Logo and school name
      &-brand {
        display: flex;
        align-items: center;
        flex-basis: 12.5rem;
        height: 100%;
        font-size: .75rem;
        overflow: hidden;
        margin: 0;
        margin-inline-start: 1rem;

        > img {
          object-fit: contain;
          height: 100%;
          max-width: 4.5rem;
        }

        .district-name {
          font-family: $font-lato-bold;
          line-height: 1.25;
          color: $gray-600;
          max-width: 8.5rem;
          white-space: normal;
          transition: color 0.15s ease-in-out;
          text-decoration: none;

          &:not(.disabled):hover {
            color: var(--scheme-top-nav-txt);
          }
        }
      }

      .icon-with-action {
        height: 100%;
        border: 2px solid transparent;
        padding: .25rem;

        &:hover {
          color: var(--scheme-top-nav-txt);
        }

        &:focus {
          border: 2px solid var(--scheme-top-nav-txt);
        }
      }

      #nav-main {
        display: none;
        flex-wrap: nowrap;
      }

      #nav-service {
        flex-grow: 1;
        justify-content: flex-end;
        flex-wrap: nowrap;

        // Search
        #search-form {
          color: $gray-600;
          position: relative;

          .icon-with-action {
            padding: .125rem .375rem;
            position: absolute;
          }

          input {
            color: inherit;
            font-size: .75rem;
            width: 7.5rem;
            height: calc(1.5em + .5rem + 2px);
            padding-left: 1.75rem;

            &:focus {
              border: 2px solid var(--scheme-top-nav-txt);
              box-shadow: unset;
            }

            &::placeholder {
              font-style: italic;
            }

            &::-webkit-input-placeholder {
              font-style: italic;
            }

            &:-moz-placeholder {
              font-style: italic;
            }

            &::-moz-placeholder {
              font-style: italic;
            }

            &:-ms-input-placeholder {
              font-style: italic;
            }
          }
        }

        #header-notifications {
          height: 100%;
        }

        .dropdown {
          .dropdown-toggle {
            box-shadow: none;
            font-size: 1rem;
            padding: .25rem;
          }

          &.active .dropdown-toggle {
            background-color: transparent;
          }
        }

        .nav-item {
          font-size: 1rem;

          .nav-link {
            padding: .5rem .375rem;
            align-self: center;
            color: $gray-600;
          }
        }

        // User name
        #user-settings {
          width: rem-calc(68);
          margin: rem-calc(14) 0;
          display: none;
          align-items: center;
          justify-content: center;

          .user-settings-name {
            display: flex;
            font-size: $font-14;
            font-family: $font-lato-bold;
            margin: -.75rem -.75rem .75rem;
            padding: .75rem 1rem;
            color: var(--scheme-top-nav-txt);
            background: var(--scheme-top-nav-active-bg-lt);
            border-radius: 3px 3px 0 0;
          }

          .dropdown-toggle {
            min-height: rem-calc(36);
            min-width: rem-calc(36);
            max-height: rem-calc(36);
            max-width: rem-calc(36);
            border: 2px solid transparent;
            border-radius: 50%;
            padding: 0;
            font-family: $font-lato-bold;
            font-size: $font-14;
            text-transform: uppercase;
            color: var(--scheme-top-nav-txt);
            background: var(--scheme-top-nav-active-bg-lt);
            display: flex;
            justify-content: center;
            align-items: center;

            &:hover, &:focus {
              border: 2px solid var(--scheme-top-nav-txt);
            }
          }

          &.show .dropdown-toggle {
            border: 2px solid var(--scheme-top-nav-txt);
          }

          .dropdown-menu {
            right: 1rem;
            top: rem-calc(44);
          }

          .nav-link {
            font-size: .75rem;
            font-family: $font-lato;
            white-space: normal;
            padding: 0;
          }
        }

        .atlas-logo-public {
          display: flex;
          flex-direction: column;
          justify-content: center;
          font-size: $font-xs;
          margin: 0 1rem;
          padding-bottom: .125rem;

          &--text {
            color: $text-color-gray;
          }
        }
      }

      #nav-main-mobile {
        display: block;
        position: absolute;
        top: 4.25rem;
        min-height: calc(100vh - 4.25rem);
        width: 100%;
        background-color: $white;
        z-index: 1025;

        .mobile-nav-item {
          box-shadow: 0 1px 0 $gray-300;

          & > button {
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: .75rem 1rem;
            border-radius: 0;
            color: $gray-600;
            height: auto;

            &:focus-visible, &:focus {
              border-color: var(--scheme-top-nav-active-bg-lt);
              box-shadow: unset;
            }

            &.active:not(:hover) {
              color: var(--scheme-top-nav-txt);
              background-color: var(--scheme-top-nav-active-bg-lt);
            }

            span {
              text-align: start;
              padding: 0 .5rem;
            }

            .icon {
              min-width: 1.25rem;
              width: 1.25rem;
              text-align: start;
            }

            .dropdown-icon:before {
              content: "\f105";
            }

            &.not-collapsed {
              color: var(--scheme-top-nav-txt);

              .dropdown-icon:before {
                content: "\f107";
              }
            }
          }

          .sub-link-wrapper {
            padding: .5rem 1rem;
            display: flex;
            flex-direction: column;

            .mobile-nav-sub-link {
              color: $gray-600;
              display: flex;
              align-items: center;
              padding: .375rem 1rem;
              font-size: 1rem;
              text-decoration: none;
              border: 1px solid transparent;
              border-radius: $border-m;

              &.sub-link {
                padding-left: 2rem;
              }

              &:hover {
                color: var(--scheme-top-nav-txt);
                background-color: var(--scheme-top-nav-active-bg-lt);
              }

              span {
                padding: 0 .5rem;
              }

              .icon-bullet {
                font-size: .375rem;
              }

            }
          }

          & > a {
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: .75rem 1rem;
            border-radius: 0;
            color: $gray-600;
            line-height: 1.5;
            font-weight: 400;
            font-size: 1rem;
            font-family: $font-lato-bold;

            span {
              text-align: start;
              padding: 0 1rem;
            }

            .icon {
              min-width: 1.25rem;
              width: 1.25rem;
              text-align: start;
            }
          }
        }
      }

      .nav {
        height: 100%;

        // Dropdown
        .b-nav-dropdown, .b-dropdown {
          &.active {
            .dropdown-toggle:not(:hover) {
              color: var(--scheme-top-nav-txt);
              background-color: var(--scheme-top-nav-active-bg-lt);
            }
          }

          .dropdown-toggle {
            height: 100%;
            box-shadow: none;
            font-family: $font-lato;
            font-size: .875rem;

            color: $gray-600;
            background-color: $white;
            border: 2px solid transparent;
            border-radius: 0;

            display: flex;
            align-items: center;

            &:hover {
              color: var(--scheme-top-nav-txt);
            }

            &[aria-expanded='true'] {
              color: var(--scheme-top-nav-txt);
            }

            &:focus-visible, :focus {
              border-color: var(--scheme-top-nav-active-bg-lt);
              box-shadow: unset;
            }
          }

          .dropdown-menu {
            border: 1px solid var(--scheme-top-nav-txt);
            padding: .75rem;

            li {
              a.dropdown-item {
                font-size: 1rem;
                line-height: 2;
                color: $gray-600;
                background-color: $white;
                border: 1px solid transparent;
                border-radius: $border-m;
                padding: 0 .625rem;

                &:hover {
                  color: var(--scheme-top-nav-active-txt);
                  background-color: var(--scheme-top-nav-active-bg-dk);
                  border-color: var(--scheme-top-nav-active-bg-dk);
                }

                &:focus-visible, &:focus {
                  border-color: var(--scheme-top-nav-active-bg-lt);
                  box-shadow: unset;
                }
              }

              &.sub-link {
                padding: 0 1rem;
              }
            }
          }
        }
      }

      .atlas-logo {
        height: 1.5rem;
      }

      .menu-toggle {
        padding: 1rem .75rem;
        font-size: 1rem;

        &.active {
          color: var(--scheme-top-nav-txt);
        }
      }

      #atlas-logo-print {
        display: none;
      }
    }
  }
}

@media (max-width: 1360px) {
  .navbar #nav-main .b-dropdown .item-name {
    font-size: .75rem;
  }
}

@media (max-width: 1200px) {
  .navbar:not(.public) {
    .icon-with-action {
      padding: .5rem;
    }

    #nav-main .b-dropdown > .dropdown-toggle {
      padding: .5rem .75rem;
      font-size: 1rem;

      .item-name {
        display: none;
      }
    }

    #nav-service {
      #header-notifications .icon-with-action {
        padding: .5rem;
      }

      .dropdown .dropdown-toggle {
        padding: .5rem;
      }
    }
  }
}

@media (max-width: 991px) {
  .navbar:not(.public) {
    #nav-service #search-form {
      height: 100%;

      input {
        display: none;
      }

      .icon-with-action {
        font-size: 1rem;
        padding: .5rem;
        position: initial !important;
      }
    }
  }
}

@media (max-width: 800px) {
  .navbar.public {
    .icon-with-action {
      padding: .5rem;
    }

    & > #nav-main .b-dropdown > .dropdown-toggle {
      padding: .5rem .75rem;
      font-size: 1rem !important;

      .item-name {
        display: none;
      }
    }

    & > #nav-service .dropdown .dropdown-toggle {
      padding: .5rem;
    }
  }
}

@media (max-width: 620px) {
  .navbar.public > #nav-service {

    .menu-toggle {
      padding: 1rem .5rem !important;
      margin-inline-end: 0;
    }

    .atlas-logo {
      height: 1.25rem;
    }

    #search-form {
      height: 100%;

      input {
        display: none;
      }

      .icon-with-action {
        font-size: 1rem;
        position: initial !important;
      }
    }
  }
}

html[dir="rtl"] {
  #app .app-header .navbar #nav-service {
    #search-form .icon-with-action {
      left: 0;
    }

    #user-settings .dropdown-menu {
      left: 1rem;
      right: auto;
    }
  }
}

@media print {
  .app-header {
    margin: 0 1rem;
    border-bottom: 1px solid $gray-300;
    page-break-after: avoid;

    .navbar {
      display: flex !important;
      justify-content: space-between;

      #nav-main, #nav-service, .menu-toggle {
        display: none !important;
      }

      &-brand {
        margin-inline-start: 0 !important;

        .district-name {
          max-width: initial !important;
        }
      }

      #atlas-logo-print {
        display: flex !important;
        align-items: center;

        .logo {
          width: auto;
          height: 2.25rem;
        }

        span {
          margin: 0 .25rem;
          font-family: $font-lato-bold;
          line-height: 1.25;
        }
      }
    }
  }
}
</style>
